import PageLayout from 'components/pages/PageLayout'
import React from 'react'
import { GetStaticPathsResult, NextPage } from 'next'
import { withApollo } from 'components/withApollo'
import PageMeta from 'components/PageMeta'
import { useRouter } from 'next/router'
import { useQuery } from '@apollo/client'
import getStaticApolloProps from 'lib/getStaticApolloProps'
import createApolloClient from 'components/withApollo/createApolloClient'
import { marketPathsQuery } from 'components/pages/MarketPage/types/marketPathsQuery'
import MARKET_PATHS_QUERY from 'components/pages/MarketPage/marketPathsQuery'
import {
  marketQuery,
  marketQueryVariables
} from 'components/pages/MarketPage/types/marketQuery'
import NotFoundPage from 'components/pages/NotFoundPage'
import LotsList from 'components/LotsList'
import TrucksList from 'components/TrucksList'
import TrucksMap from 'components/pages/MarketPage/TrucksMap'
import ButtonLink from 'components/ui/ButtonLink'
import { TruckIcon } from '@heroicons/react/outline'
import ErrorPage from 'components/pages/ErrorPage'
import CateringRatings from 'components/CateringRatings'
import CtaSection from 'components/pages/BlogPostPage/CtaSection'
import MarketHeroSection from 'components/MarketHeroSection'

import MARKET_QUERY from './query'

type Props = {}

type Params = {
  market: string
}

const MarketPage: NextPage = () => {
  const router = useRouter()

  const { market: slug } = router.query as Params

  const { data, error } = useQuery<marketQuery, marketQueryVariables>(
    MARKET_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        slug
      }
    }
  )

  if (error) return <ErrorPage message={error.message} />
  if (!data?.marketBySlug) {
    return <NotFoundPage search={slug} />
  }

  const market = data.marketBySlug
  return (
    <PageLayout fullWidth gray staticPage>
      <PageMeta
        canonicalPath={market.customerPath}
        coordinates={[market.latitude, market.longitude]}
        image={market.image.medium}
        keywords={`food trucks, map, ${market.name}, ${market.stateFull}, catering`}
        ldJson={data.structuredData.marketPage}
        title={`Best Food Trucks | Food trucks in ${market.name}`}
      />
      <div className="w-full">
        <MarketHeroSection
          isCateringPage={false}
          market={market}
          slug={slug}
          state={market.state}
        />
        <div className="mb-10 w-full">
          <div className="max-w-screen-lg mx-auto md:px-4">
            <TrucksMap
              defaultCenter={{ lat: market.latitude, lng: market.longitude }}
              market={market}
            />
          </div>

          {market.lots.total > 1 ? (
            <LotsList marketId={market.id} marketName={market.name} />
          ) : (
            <div className="py-10 bg-gray-50">
              <div className="max-w-screen-lg mx-auto md:px-4 pt-4">
                <div className="prose mb-6">
                  <h2 className="px-4 md:px-0">Browse all food trucks:</h2>
                </div>
                <TrucksList marketId={market.id} />
              </div>
            </div>
          )}

          <div className="py-10 bg-white">
            <div className="max-w-screen-lg mx-auto md:px-4 pt-4">
              <div className="flex flex-col items-center md:mb-10 mb-6">
                <div className="p-3 rounded-full bg-gray-700">
                  <TruckIcon className="md:w-7 md:h-7 h-6 w-6 text-gray-50" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold leading-5 text-gray-700 mt-5 text-center px-4">
                  Food Trucks in {market.name}
                </h2>
              </div>

              {market.description?.html && market.description?.html.length > 0 && (
                <div
                  className="prose max-w-none md:px-0 px-4 mb-12"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    // eslint-disable-next-line react/no-danger
                    __html: market.description?.html
                  }}
                />
              )}

              {market.topTrucksEnabled && (
                <div className=" max-w-none mb-10">
                  <div className="prose my-2 max-w-none mb-4 px-4 md:px-0">
                    <h2>BFT recommends:</h2>
                    <p>
                      Using customer reviews and our extensive orders history we
                      have compiled a list of top trucks in {market.name} that
                      we think you can&apos;t miss. Check out what&apos;s hot by
                      clicking the button below:
                    </p>
                  </div>

                  <div className="text-center">
                    <ButtonLink
                      color="primary"
                      href={market.customerTopTrucksPath}
                      size="large"
                      variant="contained"
                    >
                      <p>
                        Top {market.marketTopTrucks.length} food trucks in{' '}
                        {market.name}
                      </p>
                    </ButtonLink>
                  </div>
                </div>
              )}
            </div>
            <CtaSection />
            {market.lots.total > 1 ? (
              <div className="py-10 bg-white">
                <div className="max-w-screen-lg mx-auto md:px-4 pt-4">
                  <div className="prose mb-6">
                    <h2 className="px-4 md:px-0">Browse all food trucks:</h2>
                  </div>
                  <TrucksList marketId={market.id} />
                </div>
              </div>
            ) : (
              <LotsList marketId={market.id} marketName={market.name} whiteBg />
            )}
          </div>

          <CateringRatings marketId={market.id} />
        </div>
      </div>
    </PageLayout>
  )
}

export default withApollo({ ssr: false })(MarketPage as NextPage)

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  const client = createApolloClient({}, null)

  const { data } = await client.query<marketPathsQuery>({
    query: MARKET_PATHS_QUERY
  })

  const paths = data.customerPaths.marketPaths.map((marketPath) => ({
    params: { market: marketPath }
  }))

  return {
    paths,
    fallback: 'blocking'
  }
}

export const getStaticProps = getStaticApolloProps<Props, Params>(MarketPage, {
  revalidate: 60 * 30 // 30 minutes
})
